import { Fragment, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { getVendorInitiatedReversals } from '../../services/ReversalService'
import { scrollToPageTop } from '../../utils/scrollToPageTop'
import { formatAmount, formatDate, formatPerson } from './reversals/formatting'
import PaginationBar from './reversals/PaginationBar'
import { TableEmptyRow, TableErrorRow, TableLoadingRow } from './reversals/TableStates'
import CreateVendorReversalModal from './reversals/CreateVendorReversalModal'
import ReversalDetailsModal from './reversals/ReversalDetailsModal'

const VendorInitiatedReversals = () => {
    const [page, setPage] = useState(1)
    const [showCreateModal, setShowCreateModal] = useState(false)
    const [selectedReversal, setSelectedReversal] = useState(null)

    const { data, isPending, isError, isFetching } = useQuery({
        queryKey: ['reversals', 'vendor-initiated', page],
        queryFn: () => getVendorInitiatedReversals(page),
        placeholderData: keepPreviousData,
        staleTime: 0,
    })

    const reversals = data?.data?.data ?? []
    const lastPage  = data?.data?.meta?.last_page ?? 1

    const renderRows = () => {
        if (isPending)              return <TableLoadingRow colSpan={5} />
        if (isError)                return <TableErrorRow colSpan={5} />
        if (reversals.length === 0) return <TableEmptyRow colSpan={5} message="No vendor refunds recorded yet." />

        return reversals.map((reversal) => {
            const recordedAt = reversal.initiated_at ?? reversal.created_at
            const open = () => setSelectedReversal(reversal)
            return (
                <tr
                    key={reversal.uuid}
                    onClick={open}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open() } }}
                    tabIndex={0}
                    role="button"
                    aria-label="Open reversal details"
                    style={{ cursor: 'pointer' }}
                >
                    <td>
                        <div
                            className="d-xl-none p-3 my-1 rounded-3 shadow-sm"
                            style={{
                                background:  'linear-gradient(135deg, rgba(40,167,69,0.04), rgba(40,167,69,0.09))',
                                borderLeft:  '4px solid #28a745',
                                border:      '1px solid rgba(40,167,69,0.15)',
                            }}
                        >
                            <div className="d-flex justify-content-between align-items-center gap-3 mb-3">
                                <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                                    <i className="bi bi-arrow-down-circle-fill text-success" aria-hidden="true" style={{ fontSize: '1.4rem' }} />
                                    <span className="text-success fw-bold" style={{ fontSize: '1.15rem' }}>
                                        {formatAmount(reversal.inbound_amount)}
                                    </span>
                                </div>
                                <span className="badge badge-success light text-nowrap">Refund</span>
                            </div>

                            <div className="mb-2">
                                <div className="text-uppercase text-muted" style={{ fontSize: '0.68rem', letterSpacing: '0.06em', fontWeight: 600 }}>Refund ID</div>
                                <div className="text-body-secondary fw-semibold" style={{ fontSize: '0.95rem', letterSpacing: '0.08em', wordBreak: 'break-all' }}>
                                    {reversal.inbound_mpesa_receipt_number}
                                </div>
                            </div>

                            {reversal.outbound_mpesa_receipt_number && (
                                <div className="mb-2">
                                    <div className="text-uppercase text-muted" style={{ fontSize: '0.68rem', letterSpacing: '0.06em', fontWeight: 600 }}>Original Receipt ID</div>
                                    <div
                                        className="fw-bold text-body d-inline-block px-2 py-1 rounded"
                                        style={{
                                            fontSize:        '1rem',
                                            letterSpacing:   '0.08em',
                                            wordBreak:       'break-all',
                                            backgroundColor: 'rgba(40,167,69,0.08)',
                                            border:          '1px solid rgba(40,167,69,0.2)',
                                        }}
                                    >
                                        {reversal.outbound_mpesa_receipt_number}
                                    </div>
                                </div>
                            )}

                            <div
                                className="d-flex justify-content-between align-items-center gap-2 flex-wrap mt-3 pt-2"
                                style={{ borderTop: '1px solid rgba(0,0,0,0.05)' }}
                            >
                                <div className="text-muted small">
                                    <i className="bi bi-calendar-event me-1" aria-hidden="true" />
                                    {recordedAt ? formatDate(recordedAt) : '-'}
                                </div>
                                <div className="text-muted small text-end" style={{ wordBreak: 'break-word' }}>
                                    <i className="bi bi-person me-1" aria-hidden="true" />
                                    {formatPerson(reversal.initiated_by)}
                                </div>
                            </div>
                        </div>
                        <span className="d-none d-xl-inline text-body-secondary fw-semibold" style={{ letterSpacing: '0.08em', wordBreak: 'break-word' }}>
                            {reversal.inbound_mpesa_receipt_number}
                        </span>
                    </td>
                    <td className="d-none d-xl-table-cell fw-semibold" style={{ letterSpacing: '0.08em', wordBreak: 'break-word' }}>
                        {reversal.outbound_mpesa_receipt_number || '-'}
                    </td>
                    <td className="d-none d-xl-table-cell fw-semibold" style={{ wordBreak: 'break-word' }}>
                        {formatAmount(reversal.inbound_amount)}
                    </td>
                    <td className="d-none d-xl-table-cell" style={{ wordBreak: 'break-word' }}>
                        {formatPerson(reversal.initiated_by)}
                    </td>
                    <td className="d-none d-xl-table-cell" style={{ wordBreak: 'break-word' }}>
                        {recordedAt ? formatDate(recordedAt) : '-'}
                    </td>
                </tr>
            )
        })
    }

    return (
        <Fragment>
            <div className="d-flex align-items-center gap-2 mb-3">
                <button
                    type="button"
                    onClick={() => setShowCreateModal(true)}
                    className="btn btn-sm btn-primary ms-auto d-none d-xl-inline-flex align-items-center"
                >
                    <i className="fa fa-plus me-1" aria-hidden="true" />
                    Record Refund
                </button>
                <button
                    type="button"
                    onClick={() => setShowCreateModal(true)}
                    className="btn btn-primary rounded-circle ms-auto d-inline-flex d-xl-none align-items-center justify-content-center shadow-sm"
                    style={{ width: '2.5rem', height: '2.5rem', padding: 0 }}
                    title="Record Refund"
                    aria-label="Record Refund"
                >
                    <i className="fas fa-plus" aria-hidden="true" />
                </button>
            </div>

            <table className="table verticle-middle mb-0" style={{ tableLayout: 'fixed', width: '100%' }}>
                <thead className="thead-xl-only">
                    <tr>
                        <th scope="col" style={{ width: '22%' }}>Refund ID</th>
                        <th scope="col" style={{ width: '22%' }}>Original Receipt ID</th>
                        <th scope="col" style={{ width: '14%' }}>Amount</th>
                        <th scope="col" style={{ width: '22%' }}>Recorded by</th>
                        <th scope="col" style={{ width: '20%' }}>Recorded</th>
                    </tr>
                </thead>
                <tbody>{renderRows()}</tbody>
            </table>
            <PaginationBar
                page={page}
                lastPage={lastPage}
                isLoading={isFetching}
                onPrev={() => { setPage(p => Math.max(p - 1, 1)); scrollToPageTop(); }}
                onNext={() => { setPage(p => Math.min(p + 1, lastPage)); scrollToPageTop(); }}
            />

            <CreateVendorReversalModal
                show={showCreateModal}
                onHide={() => setShowCreateModal(false)}
            />

            <ReversalDetailsModal
                show={selectedReversal !== null}
                onHide={() => setSelectedReversal(null)}
                variant="vendor"
                reversal={selectedReversal}
            />
        </Fragment>
    )
}

export default VendorInitiatedReversals
